const CORE = [
    "browser_protection",
    "local_detection",
    "standard_policies",
    "event_log",
    "admin_dashboard",
];
export const PLANS = {
    early_access: {
        id: "early_access",
        name: "Early Access",
        priceDisplay: "Free",
        maxUsers: 3,
        features: [...CORE],
        selfServe: false, // assigned, not purchasable
    },
    starter: {
        id: "starter",
        name: "Starter",
        priceDisplay: "£59/mo",
        maxUsers: 5,
        features: [...CORE],
        selfServe: true,
        stripePriceIdEnv: "STRIPE_PRICE_STARTER",
    },
    team: {
        id: "team",
        name: "Team",
        priceDisplay: "£199/mo",
        maxUsers: 25,
        features: [
            ...CORE,
            "custom_policies",
            "document_upload_protection",
            "false_flag_review",
            "team_management",
        ],
        selfServe: true,
        stripePriceIdEnv: "STRIPE_PRICE_TEAM",
    },
    enterprise: {
        id: "enterprise",
        name: "Enterprise",
        priceDisplay: "Custom",
        maxUsers: Number.POSITIVE_INFINITY,
        features: [
            ...CORE,
            "custom_policies",
            "document_upload_protection",
            "false_flag_review",
            "team_management",
            "sso",
            "scim",
            "custom_packs",
            "self_hosted_verifier",
        ],
        selfServe: false,
        stripePriceIdEnv: "STRIPE_PRICE_ENTERPRISE",
    },
};
/** Normalise any historical plan value onto an active plan. */
export function resolvePlan(plan) {
    switch (plan) {
        case "starter":
        case "team":
        case "enterprise":
        case "early_access":
            return plan;
        case "pro":
            // Historical "Pro" tier was Team-equivalent.
            return "team";
        case "trial":
        case undefined:
        default:
            return "early_access";
    }
}
/** Does the given workspace have access to `feature`? */
export function hasFeature(workspace, feature) {
    const plan = resolvePlan(workspace?.plan);
    return PLANS[plan].features.includes(feature);
}
/** Seat cap for the workspace's plan. */
export function seatCap(workspace) {
    return PLANS[resolvePlan(workspace?.plan)].maxUsers;
}
/** Plan definition for the workspace. */
export function planFor(workspace) {
    return PLANS[resolvePlan(workspace?.plan)];
}
/** Convenience for callable handlers — throws a structured error if the
 *  workspace can't use `feature`. Callers should catch and rethrow as
 *  HttpsError("permission-denied", ...) with code `plan-upgrade-required`. */
export class FeatureLockedError extends Error {
    feature;
    currentPlan;
    code = "plan-upgrade-required";
    constructor(feature, currentPlan) {
        super(`Feature "${feature}" requires a plan upgrade. Current plan: ${currentPlan}.`);
        this.feature = feature;
        this.currentPlan = currentPlan;
    }
}
export function requireFeature(workspace, feature) {
    if (!hasFeature(workspace, feature)) {
        throw new FeatureLockedError(feature, resolvePlan(workspace?.plan));
    }
}
export class SeatLimitError extends Error {
    cap;
    currentPlan;
    code = "seat-limit-reached";
    constructor(cap, currentPlan) {
        super(`Seat limit reached (${cap}) on plan ${currentPlan}. Upgrade to invite more users.`);
        this.cap = cap;
        this.currentPlan = currentPlan;
    }
}
/** Plans listed in ascending order of capability — used by UI helpers. */
export const PLAN_ORDER = ["early_access", "starter", "team", "enterprise"];
/** "Is plan A at least as good as plan B?" */
export function planAtLeast(a, b) {
    return PLAN_ORDER.indexOf(resolvePlan(a)) >= PLAN_ORDER.indexOf(b);
}
